import { ScrollSide } from '@/webgl/runner/entities/BackgroundEntity'
import { BonusType } from '@/webgl/runner/entities/BonusEntity'
import { CollectibleType } from '@/webgl/runner/entities/CollectibleEntity'
import { CollectibleResourceType as CollectibleResourceType } from '@/webgl/runner/rendering/CollectibleRenderer'
import { CompleteCondition, DelayResolution, SequenceViewVariant } from '@/webgl/runner/scenarios/SequenceConfig.types'

import { ScenarioSetup } from '../ScenarioSetup.types'

const { Standard, Obstacle, TurningPoint, Bonus } = CollectibleType
const { Text, Pack } = CollectibleResourceType

const ALL = [
  'arms_01',
  'arms_02',
  'arms_03',
  'arms_04',
  'arms_05',
  'arms_06',
  'legs_01',
  'legs_02',
  'legs_03',
  'legs_04',
  'legs_05',
  'legs_06',
]

const ARMS = ['arms_01', 'arms_02', 'arms_03', 'arms_04', 'arms_05', 'arms_06']

const LEGS = ['legs_01', 'legs_02', 'legs_03', 'legs_04', 'legs_05', 'legs_06']

function commonSequenceConfig() {
  return {
    condition: { id: 'blocks' } as CompleteCondition,
    preserveCollectibles: true,
    resolution: { id: 'delay', duration: 1.5 } as DelayResolution,
    scrolling: [
      'yoga_mat',
      'mirror_01_left',
      'mirror_02_left',
      'spotlight_left',
      'mirror_01_right',
      'mirror_02_right',
      'spotlight_right',
      'dance_shoes',
      'radio',
      'speakers',
    ],
    view: { variant: SequenceViewVariant.Default },
  }
}

const config: ScenarioSetup = {
  backgroundColor: 0x4ea4bd,
  lanesColor: 0xfed1c8,
  lanesExtraColor: 0xfff1e3,
  lanesTextureId: 'wood',

  clearColor: 0xfff4e5,
  smokeColor: 0xf7ead8,

  playerColor: 0x4ea4bd,
  playerGoodColor: 0xfce5ab,
  playerWrongColor: 0xfe8769,

  audioSfx: { path: 'sfx_audio/sfx.json' },
  audio: {
    en: {
      path: 'caris_2/audios/en/bundle_1.json',
      subtitles: 'caris_2/audios/en/bundle_1.vtt',
    },
  },

  collectibles: {
    resources: [{ request: 'caris_2/collectible.glb' }],

    items: [
      { id: 'arms_01', type: Standard },
      { id: 'arms_02', type: Standard },
      { id: 'arms_03', type: Standard },
      { id: 'arms_04', type: Standard },
      { id: 'arms_05', type: Standard },
      { id: 'arms_06', type: Standard },
      { id: 'legs_01', type: Standard },
      { id: 'legs_02', type: Standard },
      { id: 'legs_03', type: Standard },
      { id: 'legs_04', type: Standard },
      { id: 'legs_05', type: Standard },
      { id: 'legs_06', type: Standard },
      {
        id: 'phone_player',
        type: Obstacle,
        bonus: BonusType.Focus,
        chill: true,
        audio: 'TYCE-caris_one-two-three-four-1-1',
      },
      { id: 'muscle_memory', type: Obstacle, bonus: BonusType.Focus, chill: true },
      //TODO: Replace this with mirror when asset is ready
      { id: 'mirror', type: Obstacle, bonus: BonusType.Mirror, audio: 'TYCE-caris_oh-no-we-have-to-rehearse-1' },
      { id: 'confusion', type: Obstacle, bonus: BonusType.Confusion, audio: 'TYCE-caris_is-this-right-1' },
    ],
  },

  particles: {
    resources: [{ id: 'default', request: 'common/particles_music.glb', scale: 0.5 }],
  },

  background: {
    resources: [
      {
        id: 'background',
        request: 'caris_2/background.glb',
      },
      {
        id: 'dance_shoes',
        request: 'caris_2/dance_shoes.glb',
        animation: { framerate: 12, loop: true },
      },
      {
        id: 'radio',
        request: 'caris_2/radio.glb',
        animation: { framerate: 12, loop: true },
      },
      {
        id: 'speakers',
        request: 'caris_2/speakers.glb',
        animation: { framerate: 12, loop: true },
      },
    ],
    items: [
      { id: 'yoga_mat' },
      // { id: "mirror_01_left", side: ScrollSide.LEFT, scale: 0.9 },
      { id: 'mirror_02_left', side: ScrollSide.LEFT, scale: 0.9 },
      // { id: "mirror_01_right", side: ScrollSide.RIGHT, scale: 0.9 },
      { id: 'mirror_02_right', side: ScrollSide.RIGHT, scale: 0.9 },
      { id: 'spotlight_right', side: ScrollSide.RIGHT },
      { id: 'spotlight_left', side: ScrollSide.LEFT },
      { id: 'dance_shoes', scale: 0.6 },
      { id: 'radio', scale: 0.6 },
      { id: 'speakers', scale: 0.6 },
    ],
  },

  speed: {
    min: 3.5,
    max: 4.5,
  },

  sequences: [
    // #region dance_1_1
    {
      id: 'dance_1_1',
      ...commonSequenceConfig(),
      goals: [['arms_01', 'legs_01']],
      goalText: 'Collect moves in this order:',
      track: {
        type: 'gate-track',
        gates: [
          { pool: ARMS, goals: ['arms_01'] },
          { pool: LEGS, goals: ['legs_01'] },
        ],
        rate: 8,
      },
    },

    // #region dance_1_2
    {
      id: 'dance_1_2',
      ...commonSequenceConfig(),
      goals: [['arms_02', 'legs_02']],
      goalText: 'Collect moves in this order:',
      track: {
        type: 'gate-track',
        gates: [
          { pool: ARMS, goals: ['arms_02'] },
          { pool: LEGS, goals: ['legs_02'] },
        ],
        rate: 8,
      },
    },

    // #region dance_1_3
    {
      id: 'dance_1_3',
      ...commonSequenceConfig(),
      goals: [['arms_06', 'legs_06']],
      goalText: 'Collect moves in this order:',
      track: {
        type: 'gate-track',
        gates: [
          { pool: ARMS, goals: ['arms_06'] },
          { pool: LEGS, goals: ['legs_06'] },
        ],
        rate: 8,
      },
    },

    // #region dance_2_1
    {
      id: 'dance_2_1',
      ...commonSequenceConfig(),
      goals: [['arms_01', 'legs_01']],
      goalText: 'Collect moves in this order:',
      stop: { preserveBonus: true },
      track: {
        type: 'gate-track',
        gates: [
          { pool: ARMS, goals: ['arms_01'] },
          { pool: LEGS, goals: ['legs_01'] },
        ],
        rate: 7,
      },
    },

    // #region dance_2_2
    {
      id: 'dance_2_2',
      ...commonSequenceConfig(),
      goals: [['arms_05', 'legs_05']],
      goalText: 'Collect moves in this order:',
      stop: { preserveBonus: true },
      track: {
        type: 'gate-track',
        gates: [
          { pool: [], goals: ['phone_player', 'phone_player', 'phone_player'] },
          { pool: ARMS, goals: ['arms_05'] },
          { pool: LEGS, goals: ['legs_05'] },
        ],
        rate: 7,
      },
    },

    // #region dance_2_3
    {
      id: 'dance_2_3',
      ...commonSequenceConfig(),
      goals: [['arms_01', 'legs_01']],
      goalText: 'Collect moves in this order:',
      track: {
        type: 'gate-track',
        gates: [
          { pool: ARMS, goals: ['arms_01'] },
          { pool: LEGS, goals: ['legs_01'] },
        ],
        rate: 7,
      },
    },

    // #region dance_3_1
    {
      id: 'dance_3_1',
      ...commonSequenceConfig(),
      goals: [['arms_03', 'legs_03']],
      goalText: 'Collect moves in this order:',
      stop: { preserveBonus: true },
      track: {
        type: 'gate-track',
        gates: [
          { pool: [], goals: ['mirror', 'mirror', 'mirror'] },
          { pool: ARMS, goals: ['arms_03'] },
          { pool: LEGS, goals: ['legs_03'] },
        ],
        rate: 7.5,
      },
    },

    // #region dance_3_2
    {
      id: 'dance_3_2',
      ...commonSequenceConfig(),
      goals: [['arms_06', 'legs_06']],
      goalText: 'Collect moves in this order:',
      stop: { preserveBonus: true },
      track: {
        type: 'gate-track',
        gates: [
          { pool: ARMS, goals: ['arms_06'] },
          { pool: LEGS, goals: ['legs_06'] },
        ],
        rate: 7.5,
      },
    },

    // #region dance_3_3
    {
      id: 'dance_3_3',
      ...commonSequenceConfig(),
      goals: [['arms_01', 'legs_01']],
      goalText: 'Collect moves in this order:',
      stop: { preserveBonus: true },
      track: {
        type: 'gate-track',
        gates: [
          { pool: [], goals: ['muscle_memory', 'muscle_memory', 'muscle_memory'] },
          { pool: ARMS, goals: ['arms_01'] },
          { pool: LEGS, goals: ['legs_01'] },
        ],
        rate: 7.5,
      },
    },

    // #region dance_3_4
    {
      id: 'dance_3_4',
      ...commonSequenceConfig(),
      goals: [['arms_02', 'legs_02']],
      goalText: 'Collect moves in this order:',
      track: {
        type: 'gate-track',
        gates: [
          { pool: ARMS, goals: ['arms_02'] },
          { pool: LEGS, goals: ['legs_02'] },
        ],
        rate: 7.5,
      },
    },

    // #region dance_4_1
    {
      id: 'dance_4_1',
      ...commonSequenceConfig(),
      goals: [['arms_03', 'legs_03', 'arms_01', 'legs_01']],
      goalText: 'Collect moves in this order:',
      goalTextDM: true,
      start: { setJauge: 0.25 },
      stop: { preserveBonus: true },
      track: {
        type: 'gate-track',
        gates: [
          { pool: ARMS, goals: ['arms_03'] },
          { pool: LEGS, goals: ['legs_03'] },
          { pool: ARMS, goals: ['arms_01'] },
          { pool: LEGS, goals: ['legs_01'] },
        ],
        rate: 7.5,
      },
    },

    // #region dance_4_2
    {
      id: 'dance_4_2',
      ...commonSequenceConfig(),
      goals: [['arms_04', 'legs_04', 'arms_02', 'legs_02']],
      goalText: 'Collect moves in this order:',
      goalTextDM: true,
      stop: { preserveBonus: true },
      track: {
        type: 'gate-track',
        gates: [
          { pool: ARMS, goals: ['arms_03'] },
          { pool: LEGS, goals: ['legs_03'] },
          { pool: ARMS, goals: ['arms_01'] },
          { pool: LEGS, goals: ['legs_01'] },
        ],
        rate: 7.5,
      },
    },

    // #region dance_4_3
    {
      id: 'dance_4_3',
      ...commonSequenceConfig(),
      goals: [['arms_02', 'legs_02', 'arms_01', 'legs_01']],
      goalText: 'Collect moves in this order:',
      goalTextDM: true,
      stop: { preserveBonus: true },
      track: {
        type: 'gate-track',
        gates: [
          { pool: [], goals: ['confusion', 'confusion', 'confusion'] },
          { pool: ARMS, goals: ['arms_02'] },
          { pool: LEGS, goals: ['legs_02'] },
          { pool: ARMS, goals: ['arms_01'] },
          { pool: LEGS, goals: ['legs_01'] },
        ],
        rate: 7.5,
      },
    },

    // #region dance_4_3
    {
      id: 'dance_4_4',
      ...commonSequenceConfig(),
      goals: [['arms_05', 'legs_05', 'arms_02', 'legs_02']],
      goalText: 'Collect moves in this order:',
      goalTextDM: true,
      track: {
        type: 'gate-track',
        gates: [
          { pool: ARMS, goals: ['arms_05'] },
          { pool: LEGS, goals: ['legs_05'] },
          { pool: ARMS, goals: ['arms_02'] },
          { pool: LEGS, goals: ['legs_02'] },
        ],
        rate: 7.5,
      },
    },
  ],

  turningPoints: [],

  audioSegments: [
    {
      id: 'audio_intro',
      audios: ['TYCE-caris_dance-is-one-1', 'TYCE-caris_Can-you-help-1'],
    },
    {
      id: 'audio_teacher',
      audios: ['TYCE_caris_and-here-we-go-dancers-1-A'],
    },
    {
      id: 'audio_solo',
      audios: ['TYCE-caris_what-if-i-forget-1'],
    },
    {
      id: 'audio_outro',
      audios: ['TYCE-caris_I-worked-hard-1'],
    },
  ],
}

export default config
